<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    toRef,
    watch,
  } from 'vue';
  import { ref } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2ReservationTagsEditor from '@/components/v2/reservations/V2ReservationTagsEditor.vue';
  import V2ReservationStatusBadge from '@/components/v2/reservations/V2ReservationStatusBadge.vue';
  import ModalsCustomer from '@/components/modals/customer/ModalsCustomer.vue';
  import { CustomersService } from '@/api/services/customers.service';
  import { useReservationGuest } from '@/composition/reservations/use-reservation-guests';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { AlertType } from '@/interfaces/IAlert';
  import { PermissionType } from '@/enums/PermissionType';
  import {
    formatDateLong,
    formatTime,
    getReservationTimeZone,
  } from '@/domain/reservations/reservation-time';
  import {
    formatReservationPhone,
    reservationPhoneDigits,
  } from '@/domain/reservations/reservation-phone';
  import type { IReservation } from '@/interfaces/IReservation';
  import type { IReservationGuestPrefill } from '@/interfaces/IReservationUi';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{ customerId: number }>();
  defineEmits<{
    (event: 'select', reservation: IReservation): void;
    (event: 'create', guest: IReservationGuestPrefill): void;
    (event: 'saved'): void;
  }>();
  const MODAL_NAME = 'v2-reservation-guest';
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const { width } = useStableWindowSize();
  const {
    guest,
    notes,
    tags,
    loading,
    saving,
    error,
    saveError,
    dirty,
    load,
    save,
  } = useReservationGuest(toRef(props, 'customerId'));
  const openingCustomer = ref(false);
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const canOpenCustomer = computed(() =>
    auth.permissionSet.has(PermissionType.CustomersManage)
  );
  const timezone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const phone = computed(() =>
    formatReservationPhone(
      guest.value?.phoneE164,
      restaurant.restaurant?.country ?? 'AR'
    )
  );
  const digits = computed(() =>
    reservationPhoneDigits(guest.value?.phoneE164 ?? '')
  );
  const sections = computed(() => [
    {
      key: 'upcoming',
      label: 'reservations.guest.upcoming',
      rows: guest.value?.upcoming ?? [],
    },
    {
      key: 'past',
      label: 'reservations.guest.past',
      rows: guest.value?.past ?? [],
    },
  ]);
  const counts = computed(() => [
    {
      label: 'reservations.guests.colVisits',
      value: guest.value?.visitsCount ?? 0,
    },
    {
      label: 'reservations.guests.colNoShows',
      value: guest.value?.noShowsCount ?? 0,
    },
    {
      label: 'reservations.guests.colCancellations',
      value: guest.value?.cancellationsCount ?? 0,
    },
    {
      label: 'reservations.guest.upcoming',
      value: guest.value?.upcomingCount ?? 0,
    },
  ]);
  const scope = computed(
    () =>
      `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}:${props.customerId}`
  );
  function bookingTimezone(booking: IReservation) {
    const branch = restaurant.branches?.find(
      (value) => value.id === booking.branchId
    );
    return getReservationTimeZone(
      branch?.location ?? restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    );
  }
  let alive = true;
  let customerSequence = 0;
  function current(key: string) {
    return alive && key === scope.value;
  }
  async function canLeave() {
    if (saving.value) return false;
    if (!dirty.value) return true;
    return modals.confirm({
      title: String(proxy.$t('posV2.discardChangesTitle')),
      message: String(proxy.$t('posV2.discardChangesConfirm')),
      acceptText: String(proxy.$t('posV2.discard')),
      acceptColor: 'error',
    });
  }
  async function close() {
    const key = scope.value;
    if ((await canLeave()) && current(key)) modals.hide(MODAL_NAME);
  }
  async function select(reservation: IReservation) {
    const key = scope.value;
    if (
      !canManage.value ||
      !(await canLeave()) ||
      !current(key) ||
      !canManage.value
    )
      return;
    proxy.$parent?.$emit('select', reservation);
    modals.hide(MODAL_NAME);
  }
  async function create() {
    const key = scope.value;
    if (
      !canManage.value ||
      !(await canLeave()) ||
      !current(key) ||
      !canManage.value ||
      !guest.value
    )
      return;
    const { customerId, name, phoneE164, email } = guest.value;
    proxy.$parent?.$emit('create', { customerId, name, phoneE164, email });
    modals.hide(MODAL_NAME);
  }
  async function saveGuest() {
    if (!canManage.value) return;
    const key = scope.value;
    const saved = await save();
    if (!current(key) || !saved) return;
    ui.showToast(
      AlertType.Success,
      String(proxy.$t('reservations.guest.saved'))
    );
    proxy.$parent?.$emit('saved');
  }
  async function openCustomer() {
    if (!canOpenCustomer.value || openingCustomer.value || !guest.value) return;
    const request = ++customerSequence;
    const key = scope.value;
    const valid = () =>
      current(key) && request === customerSequence && canOpenCustomer.value;
    openingCustomer.value = true;
    try {
      const customer = await CustomersService.getOne(props.customerId);
      if (!valid()) return;
      modals.show(
        ModalsCustomer,
        { customerToEdit: customer },
        {
          name: 'customer',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          scrollable: true,
          width: 600,
          maxWidth: Math.round(width.value * 0.95),
          clickToClose: false,
        },
        {
          saved: () => {
            if (valid()) {
              void load();
              proxy.$parent?.$emit('saved');
            }
          },
        }
      );
    } catch (err) {
      if (valid())
        ui.serverError(err, String(proxy.$t('reservations.guest.loadError')));
    } finally {
      if (current(key) && request === customerSequence)
        openingCustomer.value = false;
    }
  }
  watch(scope, () => {
    ++customerSequence;
    modals.hide(MODAL_NAME);
  });
  onScopeDispose(() => {
    alive = false;
    ++customerSequence;
  });
</script>
<template>
  <div class="reservation-guest-modal" :aria-busy="loading || saving">
    <header class="modal-header">
      <div class="header-identity">
        <h2>{{ guest?.name || $t('reservations.guest.title') }}</h2>
        <div v-if="guest" class="header-badges">
          <PosUiBadge
            v-if="guest.isClubMember || guest.club"
            color="neutral"
            variant="soft"
            >{{ $t('reservations.club.member') }}</PosUiBadge
          ><PosUiBadge
            v-if="guest.tags?.includes('vip')"
            color="neutral"
            variant="outline"
            >{{ $t('reservations.tag.vip') }}</PosUiBadge
          >
        </div>
      </div>
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="close"
        :aria-label="$t('close')"
        :disabled="saving"
        @click="close"
      />
    </header>
    <div class="modal-content">
      <div v-if="error" class="load-error" role="alert">
        <span>{{ $t('reservations.guest.loadError') }}</span
        ><PosUiButton
          color="neutral"
          variant="outline"
          :disabled="loading"
          @click="load"
          >{{ $t('reservations.page.retry') }}</PosUiButton
        >
      </div>
      <div v-if="!guest && loading" class="guest-skeletons">
        <PosUiSkeleton v-for="n in 3" :key="n" class="skeleton-block" />
      </div>
      <template v-if="guest">
        <section class="guest-contact">
          <p v-if="phone" class="contact-value">{{ phone }}</p>
          <p v-if="guest.email" class="contact-value">{{ guest.email }}</p>
          <p class="guest-since">
            {{
              $t('reservations.guest.since', {
                date: formatDateLong(guest.createdAt, timezone, $i18n.locale),
              })
            }}
          </p>
          <div v-if="digits" class="phone-actions">
            <a
              :href="`https://wa.me/${digits}`"
              target="_blank"
              rel="noopener noreferrer"
              ><PosUiIcon name="whatsapp" />{{
                $t('reservations.detail.whatsapp')
              }}</a
            ><a :href="`tel:+${digits}`"
              ><PosUiIcon name="phone-outline" />{{
                $t('reservations.detail.call')
              }}</a
            >
          </div>
        </section>
        <div class="stat-tiles">
          <div v-for="tile in counts" :key="tile.label" class="stat-tile">
            <span>{{ $t(tile.label) }}</span
            ><strong>{{ tile.value }}</strong>
          </div>
        </div>
        <section class="guest-editor">
          <label for="reservation-guest-notes">{{
            $t('reservations.guest.notes')
          }}</label
          ><PosUiTextarea
            id="reservation-guest-notes"
            v-model="notes"
            :rows="3"
            :maxlength="10000"
            :disabled="!canManage"
            :placeholder="$t('reservations.guest.notesPlaceholder')"
          />
          <h3>{{ $t('reservations.guest.tags') }}</h3>
          <V2ReservationTagsEditor v-model="tags" :disabled="!canManage" />
          <div class="save-row">
            <span v-if="saveError" class="save-error" role="alert">{{
              $t('reservations.guest.saveError')
            }}</span
            ><PosUiButton
              v-if="canManage"
              color="neutral"
              variant="outline"
              :disabled="!dirty || saving"
              @click="saveGuest"
              >{{ $t('save') }}</PosUiButton
            >
          </div>
        </section>
        <section
          v-for="section in sections"
          :key="section.key"
          class="booking-section"
        >
          <h3>{{ $t(section.label) }}</h3>
          <p v-if="!section.rows.length" class="empty-bookings">
            {{ $t('reservations.guest.noBookings') }}
          </p>
          <button
            v-for="booking in section.rows"
            :key="booking.id"
            type="button"
            class="booking-row"
            :disabled="saving || !canManage"
            @click="select(booking)"
          >
            <span class="booking-description"
              ><span class="booking-date"
                >{{
                  formatDateLong(
                    booking.startsAt,
                    bookingTimezone(booking),
                    $i18n.locale
                  )
                }}
                ·
                {{
                  formatTime(
                    booking.startsAt,
                    bookingTimezone(booking),
                    $i18n.locale
                  )
                }}</span
              ><span class="booking-summary"
                >{{
                  $tc('reservations.common.people', booking.partySize, {
                    count: booking.partySize,
                  })
                }}
                ·
                {{
                  booking.tableNames.join(', ') ||
                  $t('reservations.common.noTable')
                }}</span
              ></span
            ><V2ReservationStatusBadge
              :status="booking.status"
              :auto-closed="booking.autoClosed"
            /><PosUiIcon
              name="chevron-right"
            />
          </button>
        </section>
      </template>
    </div>
    <footer v-if="guest" class="modal-footer">
      <PosUiButton
        v-if="canOpenCustomer"
        color="neutral"
        variant="ghost"
        icon="account-outline"
        :disabled="openingCustomer || saving"
        @click="openCustomer"
        >{{ $t('reservations.guest.openCustomer') }}</PosUiButton
      ><PosUiButton
        v-if="canManage"
        icon="plus"
        :disabled="saving"
        @click="create"
        >{{ $t('reservations.guest.newBooking') }}</PosUiButton
      >
    </footer>
  </div>
</template>
<style scoped lang="scss">
  .reservation-guest-modal {
    @apply tw-flex tw-max-h-[90vh] tw-flex-col tw-bg-white;
    .modal-header {
      @apply tw-flex tw-flex-none tw-items-start tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-p-5;
    }
    .header-identity {
      @apply tw-min-w-0;
      h2 {
        @apply tw-break-words tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }
    .header-badges {
      @apply tw-mt-2 tw-flex tw-flex-wrap tw-gap-1.5;
    }
    .modal-content {
      @apply tw-min-h-0 tw-overflow-y-auto tw-p-5;
    }
    .load-error {
      @apply tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-red-700;
    }
    .guest-skeletons {
      @apply tw-flex tw-flex-col tw-gap-4;
    }
    .skeleton-block {
      @apply tw-h-24 tw-rounded-xl;
    }
    .contact-value {
      @apply tw-mb-1 tw-break-words tw-text-sm tw-text-neutral-700;
    }
    .guest-since {
      @apply tw-mb-0 tw-text-xs tw-text-neutral-500;
    }
    .phone-actions {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-gap-2;
      a {
        @apply tw-inline-flex tw-min-h-[44px] tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-px-3 tw-text-sm tw-text-neutral-700 hover:tw-bg-neutral-100;
      }
    }
    .stat-tiles {
      @apply tw-my-5 tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4;
    }
    .stat-tile {
      @apply tw-flex tw-flex-col tw-gap-1 tw-rounded-xl tw-bg-neutral-100 tw-p-3;
      span {
        @apply tw-text-xs tw-text-neutral-500;
      }
      strong {
        @apply tw-text-xl tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      }
    }
    .guest-editor {
      @apply tw-border-b tw-border-neutral-200 tw-pb-5;
      label,
      h3 {
        @apply tw-mb-2 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }
      h3 {
        @apply tw-mt-4;
      }
    }
    .save-row {
      @apply tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-3;
    }
    .save-error {
      @apply tw-text-sm tw-text-red-700;
    }
    .booking-section {
      @apply tw-mt-5;
      h3 {
        @apply tw-mb-2 tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
    }
    .empty-bookings {
      @apply tw-mb-0 tw-rounded-xl tw-bg-canvas tw-p-4 tw-text-sm tw-text-neutral-500;
    }
    .booking-row {
      @apply tw-flex tw-min-h-[64px] tw-w-full tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-neutral-100 tw-py-3 tw-text-left hover:tw-bg-neutral-100;
    }
    .booking-description {
      @apply tw-flex tw-min-w-[150px] tw-flex-1 tw-flex-col tw-gap-1;
    }
    .booking-date {
      @apply tw-text-sm tw-font-medium tw-text-neutral-800;
    }
    .booking-summary {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .modal-footer {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
    button:focus-visible,
    a:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
    }
  }
</style>
